import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import cn from 'classnames';
// Ui
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Text from 'ui3/Text/Text';
// Components
import EasyImageCrop from '../EasyImageCrop/EasyImageCrop';
// Styles
import styles from './UploadProfileImage.module.scss';

export enum UploadProfileImageSize {
  // Default crop/upload state: 352x352 px
  Default = 'default',
  // Large profile image: 240x240 px
  Large = 'large',
  // Medium profile image: 160x160 px
  Medium = 'medium',
  // Small profile image: 112x112 px
  Small = 'small',
}

type UploadProfileImageProps = {
  avatar?: File;
  imageUrl?: string;
  onFileChange: (file: File | null) => void;
  className?: string;
  size?: UploadProfileImageSize;
  editable?: boolean;
};

const UploadProfileImage = ({
  avatar,
  imageUrl,
  onFileChange,
  className,
  size = UploadProfileImageSize.Default,
  editable = true,
}: UploadProfileImageProps) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      // necessary to reset EasyImageCrop state
      onFileChange(null);
      onFileChange(acceptedFiles[0]);
    },
    maxFiles: 1,
    maxSize: 5242880, // 5MB
    accept: 'image/jpg, image/jpeg, image/png',
    disabled: !editable,
  });

  const hasImage = Boolean(imageUrl || avatar);

  const handleApplyImage = useCallback(
    (image: File) => {
      onFileChange(image);
    },
    [onFileChange]
  );

  const imagePreview = useMemo(() => {
    if (avatar) {
      return (
        <EasyImageCrop
          className={styles.imageCropper}
          imageFile={avatar}
          onApplyImage={handleApplyImage}
        />
      );
    }

    return <img src={imageUrl} alt="Profile avatar" className={styles.image} />;
  }, [avatar, handleApplyImage, imageUrl]);

  return (
    <div
      className={cn(
        styles.root,
        {
          [styles.hasImage]: hasImage,
          [styles.large]: size === UploadProfileImageSize.Large,
          [styles.medium]: size === UploadProfileImageSize.Medium,
          [styles.small]: size === UploadProfileImageSize.Small,
        },
        className
      )}
    >
      {hasImage ? (
        <div className={styles.imageContainer}>
          <div className={styles.imageFrame}>{imagePreview}</div>
          {editable && (
            <button
              type="button"
              {...getRootProps()}
              className={styles.editButton}
            >
              <input {...getInputProps()} />

              <MillionsIcon name="trash" />
            </button>
          )}
        </div>
      ) : (
        <div {...(editable ? getRootProps() : {})} className={styles.dropzone}>
          {editable && <input {...getInputProps()} />}

          <MillionsIcon name="uploadCloud" size={32} className={styles.icon} />

          <div className={styles.text}>
            <Text variant="subtitle1">Upload image</Text>

            <Text variant="body3Regular" color="lights-low">
              PNG, JPEG under 5MB.
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadProfileImage;
